import { Injectable } from '@angular/core';
import { OnFieldChanged } from '../../../../../../shared/modules/configurable-form/models/on-field-changed';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { ConfigurableFormComponent } from '../../../../../../shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { Store } from '@ngrx/store';
import { OrderBuilderState } from '../../../../../reducers';
import { getRmsRecipeSpecifications, getSpecFieldRecipe } from '../../../selectors';
import { filter, map, take, withLatestFrom, distinctUntilChanged } from 'rxjs/operators';
import { _safe } from '../../../../../../shared/utils/safe-get.method';
import {
  PUMP_NONE_CODE,
  PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
  PUMP_PRIMER_MIX,
  PUMP_PRIMER_QUANTITY,
  PUMP_PRIMER_SLUMP,
} from 'app/shared/pumping/pumping.constant';
import {
  getIndexForm,
  isOrderWithPumpPrimerProduct,
  isUnloadTypePumpValue,
  setValidatorPumpPrimer,
} from '../../../helpers/pump-primer-helper';
import { getPumpPrimerState } from '@ordersnproducts/modules/shared/modules/pump-primer/redux/pump-primer.selectors';
import { combineLatest, Subject } from 'rxjs';
import { CxModalService } from 'app/shared/cx-modal/cx-modal.service';
import { WarningRemoveAdditionalProductsModalComponent } from '@ordersnproducts/modules/shared/modules/pump-primer/modal/warning-remove-products-modal.component';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import {
  PrimerArrivalBeforeDeliveryLoaderService,
  PrimerSlumpLoaderService,
} from '@ordersnproducts/modules/shared/modules/pump-primer/services/pump-primer-mix.loader.service';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { WorkflowDcoService } from '@ordersnproducts/modules/shared/components/dco/workflow/services/workflow-dco.service';

@Injectable()
export class UnloadTypeOnChangeService implements OnFieldChanged {
  constructor(
    private store: Store<OrderBuilderState>,
    private featureToggle: FeatureToggleService,
    private workflowDcoService: WorkflowDcoService,
  ) {}

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    const isPumpPrimerEnabled = this.featureToggle.isFeatureEnabled(Features.PUMP_PRIMER);
    if (!!formGroup && isPumpPrimerEnabled) {
      const getPumpPrimerState$ = this.store.select(getPumpPrimerState);
      const getRmsSpecificationForms$ = this.store.select(getRmsRecipeSpecifications);
      const wasWarningModalClosed$ = this.workflowDcoService.didCloseWarningModal$;
      const getUnloadType$ = this.store.select(getSpecFieldRecipe(0, SpecFieldNames.UNLOAD_TYPE));
      getUnloadType$
        .pipe(
          withLatestFrom(getPumpPrimerState$),
          filter(
            ([_unloadType, _pumpPrimerState]) =>
              !!_pumpPrimerState.unloadTypePumpValues && !!formGroup.get(SpecFieldNames.UNLOAD_TYPE).value,
          ),
          withLatestFrom(getRmsSpecificationForms$, wasWarningModalClosed$),
          map(([[_unloadType, _pumpPrimerState], _forms, wasWarningModalClosed]) => {
            const index = getIndexForm(formGroup, _forms);
            const unloadTypeControl = formGroup.get(SpecFieldNames.UNLOAD_TYPE);
            const pumpPrimerProductControl = formGroup.get(PUMP_PRIMER_MIX);
            const unloadTypePumpValues = _pumpPrimerState.unloadTypePumpValues;
            const addValidator = index === 0 && isUnloadTypePumpValue(unloadTypeControl.value, unloadTypePumpValues);
            // When unload type value is changed, remove validators to pump primer fields except pump primer mix field
            setValidatorPumpPrimer(addValidator, formGroup.get(PUMP_PRIMER_MIX), Validators.required, false);
            setValidatorPumpPrimer(false, formGroup.get(PUMP_PRIMER_QUANTITY), Validators.required);
            setValidatorPumpPrimer(false, formGroup.get(PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY), Validators.required);
            setValidatorPumpPrimer(false, formGroup.get(PUMP_PRIMER_SLUMP), Validators.required);
            if (
              !wasWarningModalClosed &&
              !unloadTypeControl.pristine &&
              pumpPrimerProductControl.enabled &&
              isOrderWithPumpPrimerProduct(
                pumpPrimerProductControl.value,
                unloadTypeControl.value,
                unloadTypePumpValues,
              )
            ) {
              pumpPrimerProductControl.setValue(undefined, { emitEvent: false });
              formGroup.get(PUMP_PRIMER_QUANTITY).setValue(0, { emitEvent: false });
              formGroup.get(PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY).setValue(undefined, { emitEvent: false });
              formGroup.get(PUMP_PRIMER_SLUMP).setValue(undefined, { emitEvent: false });
            }
          }),
        )
        .subscribe();
    }
  }
}

@Injectable()
export class PumpPrimerProductOnChangeService implements OnFieldChanged {
  constructor(
    private store: Store<OrderBuilderState>,
    private cxModalService: CxModalService,
    private primerArrivalService: PrimerArrivalBeforeDeliveryLoaderService,
    private primerSlumpService: PrimerSlumpLoaderService,
  ) {}

  public removeAdditionalProducts: Subject<number> = new Subject<number>();
  public resetDcoWithPrimerMixChanged$: Subject<boolean> = new Subject<boolean>();
  public isModalDisplayed = false;

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent) {
    if (!!formGroup && !!value) {
      const getRmsSpecificationForms$ = this.store.select(getRmsRecipeSpecifications);
      const getPumpPrimerMix$ = this.store.select(getSpecFieldRecipe(0, PUMP_PRIMER_MIX));
      // Get previous value for reset in case cancel action
      const oldPrimerQty = formGroup.get(PUMP_PRIMER_QUANTITY).value;
      const oldPrimerArrival = formGroup.get(PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY).value;
      const oldPrimerSlump = formGroup.get(PUMP_PRIMER_SLUMP).value;
      let oldPrimerMix;
      const getOldValuePrimerMix$ = this.store.select(getSpecFieldRecipe(0, PUMP_PRIMER_MIX)).pipe(
        take(1),
        map((oldValue) => (oldPrimerMix = oldValue)),
      );
      getPumpPrimerMix$
        .pipe(
          distinctUntilChanged(),
          withLatestFrom(getRmsSpecificationForms$, getOldValuePrimerMix$),
          filter(
            ([_pumpPrimerMix, _forms, _getOldValuePrimerMix]) =>
              !!_pumpPrimerMix &&
              !!formGroup.get(PUMP_PRIMER_MIX).value &&
              value === _pumpPrimerMix &&
              getIndexForm(formGroup, _forms) === 0,
          ),
          map(([_pumpPrimerMix, _forms, _oldPrimerMix]) => {
            const addValidator = !!_pumpPrimerMix && _pumpPrimerMix !== PUMP_NONE_CODE;
            this.setValidatorPumpPrimerFields(formGroup, addValidator);
            if (_forms.length > 1 && _oldPrimerMix !== value && value !== PUMP_NONE_CODE && !this.isModalDisplayed) {
              this.isModalDisplayed = true;
              const modalInstance = this.cxModalService.createModal(WarningRemoveAdditionalProductsModalComponent, {
                inputs: { rtl: form.rtl },
                outputs: {
                  close: () => {
                    formGroup.get(PUMP_PRIMER_MIX).setValue(oldPrimerMix, { emitEvent: false });
                    this.revertChangesToPrimerFields(formGroup, oldPrimerQty, oldPrimerArrival, oldPrimerSlump);
                    this.setValidatorPumpPrimerFields(formGroup, addValidator);
                    this.cxModalService.removeModal(modalInstance);
                    this.isModalDisplayed = false;
                  },
                  accept: () => {
                    this.resetDcoWithPrimerMixChanged$.next(true);
                    this.removeAdditionalProducts.next(_forms[0].id);
                    this.cxModalService.removeModal(modalInstance);
                    this.isModalDisplayed = false;
                    this.initializePumpPrimerFields(formGroup, true);
                  },
                  cancel: () => {
                    formGroup.get(PUMP_PRIMER_MIX).setValue(oldPrimerMix, { emitEvent: false });
                    this.revertChangesToPrimerFields(formGroup, oldPrimerQty, oldPrimerArrival, oldPrimerSlump);
                    this.setValidatorPumpPrimerFields(formGroup, addValidator);
                    this.cxModalService.removeModal(modalInstance);
                    this.isModalDisplayed = false;
                  },
                },
              });
            } else if ((!_oldPrimerMix || _oldPrimerMix === PUMP_NONE_CODE) && value !== PUMP_NONE_CODE) {
              this.initializePumpPrimerFields(formGroup, true);
            } else if (
              !!_oldPrimerMix &&
              _oldPrimerMix !== PUMP_NONE_CODE &&
              !!_pumpPrimerMix &&
              _pumpPrimerMix !== PUMP_NONE_CODE
            ) {
              this.revertChangesToPrimerFields(formGroup, oldPrimerQty, oldPrimerArrival, oldPrimerSlump);
            }
          }),
        )
        .subscribe();
    }
  }

  private setValidatorPumpPrimerFields(formGroup: UntypedFormGroup, addValidator: boolean) {
    setValidatorPumpPrimer(addValidator, formGroup.get(PUMP_PRIMER_QUANTITY), Validators.required);
    setValidatorPumpPrimer(addValidator, formGroup.get(PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY), Validators.required);
    setValidatorPumpPrimer(addValidator, formGroup.get(PUMP_PRIMER_SLUMP), Validators.required);
  }

  private initializePumpPrimerFields(formGroup: UntypedFormGroup, initializeFields: boolean) {
    if (initializeFields) {
      const getDefaultPrimerArrival$ = this.primerArrivalService.getPrimerArrivalDefaultValue();
      const getDefaultPrimerSlump$ = this.primerSlumpService.getPrimerSlumpDefaultValue();
      const selectedPrimerArrival$ = this.primerArrivalService.getSelectedPrimerArrivalBeforeDelivery();
      const getDefaultPumpPrimerQuantity$ = this.store.select(getPumpPrimerState).pipe(
        map(({ defaultPumpPrimerQuantity }) => defaultPumpPrimerQuantity),
        take(1),
      );
      combineLatest([
        getDefaultPrimerArrival$,
        getDefaultPrimerSlump$,
        getDefaultPumpPrimerQuantity$,
        selectedPrimerArrival$,
      ])
        .pipe(
          take(1),
          map(([defaultPrimerArrival, defaultPrimerSlump, defaultPumpPrimerQuantity, selectedPrimerArrival]) => {
            const primerArrivalValue = !!selectedPrimerArrival ? selectedPrimerArrival : defaultPrimerArrival;
            formGroup.get(PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY).setValue(primerArrivalValue, { emitEvent: false });
            formGroup.get(PUMP_PRIMER_SLUMP).setValue(defaultPrimerSlump, { emitEvent: false });
            formGroup.get(PUMP_PRIMER_QUANTITY).setValue(defaultPumpPrimerQuantity, { emitEvent: false });
          }),
        )
        .subscribe();
    }
  }

  private revertChangesToPrimerFields(formGroup: UntypedFormGroup, oldPrimerQty, oldPrimerArrival, oldPrimerSlump) {
    formGroup.get(PUMP_PRIMER_QUANTITY).setValue(oldPrimerQty, { emitEvent: false });
    formGroup.get(PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY).setValue(oldPrimerArrival, { emitEvent: false });
    formGroup.get(PUMP_PRIMER_SLUMP).setValue(oldPrimerSlump, { emitEvent: false });
  }
}
